import flatten from "lodash-es/flatten";
import { all, call, put, takeLatest } from "redux-saga/effects";

import {
    ARTICLE_EXPERIENCE_PHOTO_FETCH_TRIGGER,
    ArticleExperiencePhotoFetchNormalizedResponse,
    ArticleExperiencePhotoFetchTriggerAction,
    articleExperiencePhotoFetchApi,
} from "@/mk2/apps/wiki/containers/ArticleExperiencePhoto/ArticleExperiencePhoto.actions";
import {
    XHRAction,
    XHRError,
    XHRGetError,
    handleXHRGetErrorSaga,
    normalizeError,
} from "@/mk2/helpers/api";
import { getLogger } from "@/mk2/logger";
import { ExperiencePhotoEntity, WikiArticleEntity, WikiArticleSchema } from "@/mk2/schemas";
import { getDenormalizedEntity } from "@/mk2/selectors";
import { HistoryAction } from "@/mk2/services/browserHistory";
import { wiki_api_v2_get_article_url } from "@/mk/urls/functions";

const logger = getLogger("wiki/Article.sagas");

interface ApiResponseFetchArticle {
    body: ArticleExperiencePhotoFetchNormalizedResponse;
}

function* fetch({
    articleSlug,
    categorySlug,
    experienceId,
    photoId,
    xhr,
    location,
    routeNavigate,
}: ArticleExperiencePhotoFetchTriggerAction & XHRAction & HistoryAction) {
    yield put(
        articleExperiencePhotoFetchApi.request(articleSlug, categorySlug, experienceId, photoId),
    );

    try {
        const response: ApiResponseFetchArticle = yield call(() =>
            xhr.get(wiki_api_v2_get_article_url(), { articleSlug, categorySlug }),
        );

        const experiencePhotos: ExperiencePhotoEntity[] = flatten(
            getDenormalizedEntity<WikiArticleEntity>(
                response.body,
                WikiArticleSchema,
                response.body.result.article,
            )
                .experiences.filter((exp) => exp.id == experienceId)
                .map((exp) => exp.photos),
        );
        const experiencePhoto = experiencePhotos.find((p) => p.photofile.id === photoId);
        if (!experiencePhoto) {
            const err = new Error(
                "Photo not found",
            ) as XHRError<ArticleExperiencePhotoFetchNormalizedResponse>;
            err.status = 404;
            err.response = response;
            throw err;
        }

        yield put(
            articleExperiencePhotoFetchApi.success(
                articleSlug,
                categorySlug,
                experienceId,
                photoId,
                response.body,
            ),
        );
    } catch (error) {
        yield handleXHRGetErrorSaga(error as XHRGetError, location, routeNavigate, logger);
        yield put(
            articleExperiencePhotoFetchApi.failure(
                articleSlug,
                categorySlug,
                experienceId,
                photoId,
                normalizeError(error),
            ),
        );
    }
}

export default function* root() {
    yield all([takeLatest(ARTICLE_EXPERIENCE_PHOTO_FETCH_TRIGGER, fetch)]);
}
